import {useState} from 'react'
import {formatDisplayDateTime} from '@/dayjs'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, ChevronDownIcon, RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {useGetBugReportIssues} from '../../hooks/useGetBugReportIssues'
import {BugReportIssue} from '../../types'
import {Markdown} from './Markdown'
import {
    StyledChevron,
    StyledEmptyBody,
    StyledHistoryError,
    StyledHistoryList,
    StyledHistoryRoot,
    StyledHistoryState,
    StyledIssueBody,
    StyledIssueCard,
    StyledIssueHeader,
    StyledIssueHeaderMain,
    StyledIssueHeaderRight,
    StyledIssueMeta,
    StyledIssueTitle,
    StyledStatus,
    StyledStatusDot
} from './style'

const IssueCard = ({issue}: {issue: BugReportIssue}) => {
    const [open, setOpen] = useState(false)
    const description = issue.description?.trim()

    return (
        <StyledIssueCard>
            <StyledIssueHeader type="button" onClick={() => setOpen(prev => !prev)} aria-expanded={open}>
                <StyledIssueHeaderMain>
                    <StyledIssueTitle>{issue.title}</StyledIssueTitle>
                    <StyledIssueMeta>
                        <span>{issue.identifier}</span>
                        <span>·</span>
                        <span>{formatDisplayDateTime(issue.createdAt)}</span>
                    </StyledIssueMeta>
                </StyledIssueHeaderMain>
                <StyledIssueHeaderRight>
                    {issue.state && (
                        <StyledStatus>
                            <StyledStatusDot $color={issue.state.color} />
                            {issue.state.name}
                        </StyledStatus>
                    )}
                    <StyledChevron $open={open}>
                        <ChevronDownIcon size={16} />
                    </StyledChevron>
                </StyledIssueHeaderRight>
            </StyledIssueHeader>
            {open && (
                <StyledIssueBody>
                    {description ? (
                        <Markdown content={description} />
                    ) : (
                        <StyledEmptyBody>Nessuna descrizione</StyledEmptyBody>
                    )}
                </StyledIssueBody>
            )}
        </StyledIssueCard>
    )
}

export const BugReportHistory = ({enabled}: {enabled: boolean}) => {
    const {data: issues, isLoading, isError, error, refetch, isFetching} = useGetBugReportIssues({enabled})

    if (isLoading) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryState>
                    <Spinner size={24} />
                    Caricamento segnalazioni…
                </StyledHistoryState>
            </StyledHistoryRoot>
        )
    }

    if (isError) {
        const message = error instanceof Error ? error.message : 'Errore sconosciuto'
        return (
            <StyledHistoryRoot>
                <StyledHistoryError role="alert">Impossibile caricare lo storico: {message}</StyledHistoryError>
                <Button type="button" variant="secondary" size="sm" onClick={() => refetch()}>
                    <RefreshCw01Icon size={16} />
                    Riprova
                </Button>
            </StyledHistoryRoot>
        )
    }

    if (!issues?.length) {
        return (
            <StyledHistoryRoot>
                <StyledHistoryState>
                    <AlertCircleIcon size={24} />
                    Nessuna segnalazione inviata al team di sviluppo.
                </StyledHistoryState>
            </StyledHistoryRoot>
        )
    }

    return (
        <StyledHistoryRoot>
            <StyledHistoryList>
                {issues.map(issue => (
                    <IssueCard key={issue.id} issue={issue} />
                ))}
            </StyledHistoryList>
            <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? <Spinner size={16} /> : <RefreshCw01Icon size={16} />}
                Aggiorna
            </Button>
        </StyledHistoryRoot>
    )
}
